import { Icon, SkeletonLoader } from '@toggle/design-system';
import { formatTicker, TrackingStatus } from '@toggle/toggle';
import classnames from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { AssetLogoContainer } from '~/components/asset-logo-container/AssetLogoContainer';
import { useGetInsightWidgetData } from '~/services/insights/insights-service';
import { getCardDate } from '~/utils/card-utils/card-utils';

import { alertsCardLoaderAreas } from '../../AlertsCard';
import sharedStyles from '../../AlertsCard.module.scss';
import * as S from './AlertsInsightCard.styles';

type AlertsInsightCardAlertEvent = {
  created_at?: string;
  source_id: string;
  title?: string;
  read?: boolean;
  tracking_status?: TrackingStatus;
  metadata?: {
    days_remaining?: number;
  };
  relation?: string;
};
interface AlertsInsightCardProps {
  alertEvent: AlertsInsightCardAlertEvent;
  setIsDropdownOpen?: (isDropdownOpen: boolean) => void;
  variant?: 'toast' | 'card';
  dropdown?: React.ReactNode;
}

export const AlertsInsightCard = ({
  alertEvent,
  dropdown,
  variant = 'card',
}: AlertsInsightCardProps) => {
  const { t } = useTranslation('alerts');
  const {
    created_at: createdAt,
    source_id: sourceId,
    title,
    read = false,
    tracking_status: trackingStatus,
    metadata,
    relation,
  } = alertEvent;
  const isToastVariant = variant === 'toast';

  const { data, isLoading } = useGetInsightWidgetData(sourceId);
  const entity = data?.data?.entity;

  const isTrackingActive = trackingStatus === 'active';
  const isTrackingCompleted = trackingStatus === 'completed';

  if (isLoading) {
    return <SkeletonLoader areas={alertsCardLoaderAreas} />;
  }

  if (!entity) {
    return null;
  }

  return (
    <>
      <div className={sharedStyles.header}>
        <div className={sharedStyles.headerLeft}>
          <S.AssetNameText>{entity.name}</S.AssetNameText>
          <div className={sharedStyles.dot} />
          <S.SecondaryAssetInfoText>
            {formatTicker(entity)}
          </S.SecondaryAssetInfoText>
          {relation && (
            <>
              <div className={sharedStyles.dot} />
              <S.SecondaryAssetInfoText>{relation}</S.SecondaryAssetInfoText>
            </>
          )}
        </div>
        {!isToastVariant && createdAt && (
          <div className={sharedStyles.headerRight}>
            <span className={sharedStyles.dateSpan}>
              {getCardDate(new Date(createdAt))}
            </span>
            {dropdown}
          </div>
        )}
      </div>
      <div className={sharedStyles.bodyWrapper}>
        <div className={sharedStyles.logoWrapper}>
          <AssetLogoContainer
            entity={entity}
            size={32}
            shouldUseBorderOnFallback
          />
          <S.LogoBadge>
            <Icon
              iconName="ExploreNav"
              size={12}
              fillColor="var(--icon-decorative-indigo)"
            />
          </S.LogoBadge>
        </div>
        <div className={sharedStyles.textContent}>
          <p
            className={classnames(sharedStyles.primaryText, {
              [sharedStyles.read]: read,
              [sharedStyles.isToastVariant]: isToastVariant,
            })}
          >
            {title}
          </p>
          {!isToastVariant && (trackingStatus || metadata?.days_remaining) && (
            <S.SecondaryText>
              {trackingStatus && (
                <S.TrackingStatus $isTracking={isTrackingActive}>
                  •{' '}
                  {t(
                    `alerts:alfredInsightCard.trackingStatus.${trackingStatus}`
                  )}
                </S.TrackingStatus>
              )}
              {metadata?.days_remaining !== undefined && (
                <S.Horizon>
                  ·{' '}
                  {t(
                    `alerts:alfredInsightCard.${
                      isTrackingCompleted ? 'daysElapsed' : 'daysRemaining'
                    }`,
                    {
                      count: metadata.days_remaining,
                    }
                  )}
                </S.Horizon>
              )}
            </S.SecondaryText>
          )}
        </div>
      </div>
    </>
  );
};
